import { useCallback, useMemo, useState, useEffect } from 'react';

export function useOnReplacementsRendered<T>({
	componentList = [],
	onAllChildrenRendered = undefined,
}: {
	componentList: (string | T | null | undefined)[];
	onAllChildrenRendered?: () => void;
}) {
	const [
		areAllDomReadyReplacementsRendered,
		setAreAllDomReadyReplacementsRendered,
	] = useState(false);
	const renderedItems = new Set<string>();
	const [lastComponentRenderedAt, setLastComponentRenderedAt] = useState(0);

	if (onAllChildrenRendered === undefined) {
		return { onItemRendered: () => () => {} };
	}

	const onItemRendered = useCallback(
		(id: string, onRenderedCb?: () => void) =>
			useMemo(
				() => () => {
					if (!renderedItems.has(id)) {
						renderedItems.add(id);
						const currentTime = new Date().getTime();
						setLastComponentRenderedAt(currentTime);
					}
					if (
						renderedItems.size >= componentList.length &&
						componentList.every(({ replacementId }: any) =>
							renderedItems.has(replacementId)
						)
					) {
						setAreAllDomReadyReplacementsRendered(true);
					}
					if (onRenderedCb && typeof onRenderedCb === 'function') {
						onRenderedCb();
					}
				},
				[id]
			),
		[]
	);

	useEffect(() => {
		if (componentList.length === 0) {
			setAreAllDomReadyReplacementsRendered(true);
		}
	}, [componentList.length]);

	useEffect(() => {
		if (!areAllDomReadyReplacementsRendered) return;

		onAllChildrenRendered();
	}, [areAllDomReadyReplacementsRendered]);

	return {
		onItemRendered,
		areAllDomReadyReplacementsRendered,
		lastComponentRenderedAt,
	};
}
